import { useState, useEffect, useCallback } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Building2, AlertCircle, Loader2, RefreshCw, Eye, ShieldAlert } from 'lucide-react';
import { Badge } from './ui/badge';
import { getRoleDisplayName } from '../utils/roleUtils';
import type { UserRole } from '../types';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';

interface FirmContextGateProps {
  currentUser: any;
  accessToken: string;
  children: React.ReactNode;
  onFirmResolved?: (firmId: string, firmName: string) => void;
}

/**
 * FirmContextGate ensures that firm-scoped views always have a valid consulting
 * firm context (firmId + firmName) before rendering children.
 * 
 * Behavior for NATIVE firm users:
 * - If firm context already exists → render children immediately
 * - If exactly one firm exists → auto-select and persist it
 * - If multiple firms exist → show a firm picker
 * - If zero firms exist → show a blocking error state
 * 
 * Behavior for SYSTEM ADMIN in view-as mode:
 * - NEVER auto-select a firm, even if only one exists
 * - ALWAYS show the firm picker with a "View As" banner
 * - This prevents accidental data creation under the wrong firm
 */
export function FirmContextGate({ currentUser, accessToken, children, onFirmResolved }: FirmContextGateProps) {
  const [resolving, setResolving] = useState(false);
  const [firms, setFirms] = useState<Array<{ id: string; name: string; subdomain?: string }>>([]);
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [error, setError] = useState('');
  const [resolutionMode, setResolutionMode] = useState<'checking' | 'has-context' | 'auto-selected' | 'picker' | 'missing'>('checking');

  // Detect system admin in "view as" mode
  const isViewAsMode = !!(
    currentUser?.isAdmin &&
    currentUser?.activeRole !== 'systemAdmin' &&
    currentUser?.activeRole !== 'system_admin'
  ) || currentUser?.viewAsMode === true || currentUser?.baseRole === 'systemAdmin';

  // Check if firm context already exists
  const hasFirmContext = !!(currentUser?.firmId || currentUser?.consultingFirmId);
  
  const persistFirmContext = useCallback((firmId: string, firmName: string) => {
    console.log('[VIEW_AS_CONTEXT] context persisted:', { firmId, firmName, isViewAsMode });
    console.log('[CF_ADMIN_CONTEXT] FirmContextGate persisting firm:', { firmId, firmName });
    
    // Update localStorage
    const storedUser = localStorage.getItem('pathkit_user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        userData.firmId = firmId;
        userData.consultingFirmId = firmId;
        userData.firmName = firmName;
        localStorage.setItem('pathkit_user', JSON.stringify(userData));
        console.log('[CF_ADMIN_CONTEXT] persisted firmId to localStorage:', firmId);
      } catch (e) {
        console.warn('[CF_ADMIN_CONTEXT] Failed to update localStorage with firm context');
      }
    }
    
    if (onFirmResolved) {
      onFirmResolved(firmId, firmName);
    }
  }, [onFirmResolved, isViewAsMode]);

  useEffect(() => {
    // System admin view-as mode: if they already have firm context AND viewAsMode is not
    // freshly set (i.e., they already selected a firm this session), render children.
    // But if viewAsMode is true and they DON'T have firm context, always show picker.
    if (hasFirmContext && !currentUser?.viewAsMode) {
      const firmId = currentUser.firmId || currentUser.consultingFirmId;
      console.log('[CF_ADMIN_CONTEXT] FirmContextGate: already has context, firmId:', firmId,
        isViewAsMode ? '(view-as mode, previously selected)' : '(native user)');
      setResolutionMode('has-context');
      return;
    }

    // If system admin has firm context but viewAsMode flag is still set from the switch,
    // that means they already chose — clear the flag and proceed
    if (hasFirmContext && currentUser?.viewAsMode) {
      // Clear viewAsMode flag since they've already selected
      try {
        const storedUser = localStorage.getItem('pathkit_user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          delete userData.viewAsMode;
          localStorage.setItem('pathkit_user', JSON.stringify(userData));
        }
      } catch (e) { /* ignore */ }
      const firmId = currentUser.firmId || currentUser.consultingFirmId;
      console.log('[CF_ADMIN_CONTEXT] FirmContextGate: view-as mode with firm context, firmId:', firmId);
      setResolutionMode('has-context');
      return;
    }

    // No firm context — need to resolve
    console.log('[CF_ADMIN_CONTEXT] FirmContextGate: no firm context, fetching consulting firms...');
    if (isViewAsMode) {
      console.log('[VIEW_AS_CONTEXT] firm picker shown (system admin view-as mode)');
    }
    setResolving(true);
    setResolutionMode('checking');

    (async () => {
      try {
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/consulting-firms`,
          {
            headers: getAuthHeaders(accessToken),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch consulting firms: ${response.status}`);
        }

        const data = await response.json();
        const availableFirms = (data.firms || []).filter((f: any) => f.id && f.name);
        setFirms(availableFirms);

        console.log('[CF_ADMIN_CONTEXT] FirmContextGate: found', availableFirms.length, 'firms',
          isViewAsMode ? '(view-as mode — will NOT auto-select)' : '(native user)');

        if (isViewAsMode) {
          // SYSTEM ADMIN VIEW-AS: Always show picker, never auto-select
          if (availableFirms.length > 0) {
            setResolutionMode('picker');
            console.log('[VIEW_AS_CONTEXT] firm picker shown (' + availableFirms.length + ' firms available)');
          } else {
            setResolutionMode('missing');
            console.log('[VIEW_AS_CONTEXT] no firms available for view-as mode');
          }
        } else if (availableFirms.length === 1) {
          // NATIVE USER: Auto-select the only firm
          const firm = availableFirms[0];
          setResolutionMode('auto-selected');
          console.log('[CF_ADMIN_CONTEXT] resolution mode: auto-selected, firm:', firm.name);
          persistFirmContext(firm.id, firm.name);
          
          // Reload to propagate the firm context through the full boot chain
          setTimeout(() => window.location.reload(), 300);
        } else if (availableFirms.length > 1) {
          setResolutionMode('picker');
          console.log('[CF_ADMIN_CONTEXT] resolution mode: picker (' + availableFirms.length + ' firms)');
        } else {
          setResolutionMode('missing');
          console.log('[CF_ADMIN_CONTEXT] resolution mode: missing (no firms available)');
        }
      } catch (err: any) {
        console.error('[CF_ADMIN_CONTEXT] FirmContextGate error:', err);
        setError(err.message || 'Failed to resolve consulting firm context');
        setResolutionMode('missing');
      } finally {
        setResolving(false);
      }
    })();
  }, [hasFirmContext, accessToken, currentUser, persistFirmContext, isViewAsMode]);

  const handleFirmSelected = () => {
    if (!selectedFirmId) return;
    const firm = firms.find(f => f.id === selectedFirmId);
    if (!firm) return;

    console.log('[VIEW_AS_CONTEXT] firm selected:', { firmId: firm.id, firmName: firm.name, isViewAsMode });
    persistFirmContext(firm.id, firm.name);
    
    // Reload to propagate
    window.location.reload();
  };

  // If firm context exists, render children immediately
  if (resolutionMode === 'has-context') {
    return <>{children}</>;
  }

  // Auto-selected — show brief loading state while reload happens
  if (resolutionMode === 'auto-selected') {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-600" />
          <p className="text-sm text-gray-600">Setting up firm context...</p>
        </div>
      </div>
    );
  }

  // Checking state
  if (resolutionMode === 'checking' || resolving) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="text-sm text-gray-600">Resolving consulting firm context...</p>
        </div>
      </div>
    );
  }

  // Picker — multiple firms available (or view-as mode with any number of firms)
  if (resolutionMode === 'picker') {
    return (
      <div className="flex items-center justify-center min-h-[300px] p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            {isViewAsMode && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200">
                <Eye className="h-4 w-4 text-amber-600 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-amber-800">System Admin — View As Mode</p>
                  <p className="text-[11px] text-amber-700 leading-tight mt-0.5">
                    You are viewing the platform as a firm role. Select which consulting firm context to enter.
                    Actions taken will affect the selected firm's data.
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-lg font-semibold">Select Consulting Firm</CardTitle>
            </div>
            <CardDescription className="text-sm text-gray-500 mt-1">
              {isViewAsMode
                ? 'Choose which firm to operate as. This selection controls all firm-scoped data and actions.'
                : 'Multiple consulting firms are available. Please select which firm you want to operate as.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isViewAsMode && (
              <Alert className="bg-red-50 border-red-200">
                <ShieldAlert className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 text-xs">
                  <strong>Caution:</strong> Creating or modifying data in this mode will affect the selected firm.
                  Make sure you select the correct firm before proceeding.
                </AlertDescription>
              </Alert>
            )}
            <Select value={selectedFirmId} onValueChange={setSelectedFirmId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a consulting firm..." />
              </SelectTrigger>
              <SelectContent>
                {firms.map((firm) => (
                  <SelectItem key={firm.id} value={firm.id}>
                    {firm.name} {firm.subdomain ? `(${firm.subdomain})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={handleFirmSelected} 
              disabled={!selectedFirmId}
              className="w-full"
            >
              {isViewAsMode ? `Enter firm as ${getRoleDisplayName((currentUser?.activeRole || 'cfAdmin') as UserRole)}` : 'Continue as this firm'}
            </Button>
            {isViewAsMode && (
              <p className="text-[11px] text-gray-400 text-center">
                Active role: <Badge variant="outline" className="text-[10px] px-1.5 py-0">{getRoleDisplayName((currentUser?.activeRole || 'cfAdmin') as UserRole)}</Badge>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Missing — no firms available
  return (
    <div className="flex items-center justify-center min-h-[300px] p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-lg font-semibold">Consulting Firm Required</CardTitle>
          </div>
          <CardDescription className="text-sm text-gray-500 mt-1">
            This view requires consulting firm context, but no firms are configured.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Alert className="bg-amber-50 border-amber-200">
            <AlertDescription className="text-amber-800 text-xs">
              To use CF Admin mode, at least one consulting firm must exist.
              Switch back to System Admin and create a consulting firm first,
              or contact your administrator.
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
